/**
 * DoctorApp component
 * Doctor portal for managing patients and submitting questions
 */

import React, { useState } from "react";
import { Sidebar } from "./shared/Sidebar";
import { ErrorModal } from "./ui/ErrorModal";
import { decomposerAgent, conversationAgent } from "../agents";
import { subscribeDoctorPatients } from "../services/patients";
import {
  createQuestion,
  updateQuestionStatus,
  attachConversationToQuestion,
  subscribeActiveQuestion,
  subscribeCompletedQuestions,
} from "../services/questions";
import { createConversation, addMessage } from "../services/conversations";
import { createGoals, subscribeGoals } from "../services/goals";
import { getSummary } from "../services/summaries";
import { now } from "../utils/helpers";

export const DoctorApp = ({ user, onLogout }) => {
  const [page, setPage] = useState("patients");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isLoadingPatients, setIsLoadingPatients] = useState(true);
  const [uiError, setUiError] = useState("");
  const [myPatients, setMyPatients] = useState([]);
  const [activeByPatient, setActiveByPatient] = useState({});
  const [completedByPatient, setCompletedByPatient] = useState({});
  const [goalsByQuestion, setGoalsByQuestion] = useState({});
  const [qText, setQText] = useState("");
  const [processing, setProcessing] = useState(false);
  const [summaryByQuestion, setSummaryByQuestion] = useState({});
  const [copiedInviteCode, setCopiedInviteCode] = useState(false);
  const [, forceUpdate] = useState(0);
  const refresh = () => forceUpdate((n) => n + 1);

  const copyDoctorCode = async () => {
    try {
      await navigator.clipboard.writeText(String(user?.doctorCode || ""));
      setCopiedInviteCode(true);
      setTimeout(() => setCopiedInviteCode(false), 1500);
    } catch (_error) {
      setUiError("Could not copy code automatically. Please copy it manually.");
    }
  };

  React.useEffect(() => {
    const activeUnsubs = new Map();
    const completedUnsubs = new Map();
    const goalsUnsubs = new Map();
    let retryCount = 0;
    let retryTimeout = null;

    const cleanupPatientSubs = () => {
      activeUnsubs.forEach((unsub) => unsub());
      completedUnsubs.forEach((unsub) => unsub());
      goalsUnsubs.forEach((unsub) => unsub());
      activeUnsubs.clear();
      completedUnsubs.clear();
      goalsUnsubs.clear();
    };

    const setupSubscriber = () => {
      const unsubscribePatients = subscribeDoctorPatients(
        user.id,
        (patients) => {
          setIsLoadingPatients(false);
          setUiError("");
          retryCount = 0; // Reset retry count on success
          setMyPatients(patients);
          cleanupPatientSubs();

          patients.forEach((patient) => {
            const activeUnsub = subscribeActiveQuestion(patient.id, (activeQ) => {
              setActiveByPatient((prev) => ({ ...prev, [patient.id]: activeQ }));

              if (goalsUnsubs.has(patient.id)) {
                goalsUnsubs.get(patient.id)();
                goalsUnsubs.delete(patient.id);
              }

              if (activeQ?.id) {
                const goalsUnsub = subscribeGoals(activeQ.id, (goals) => {
                  setGoalsByQuestion((prev) => ({ ...prev, [activeQ.id]: goals }));
                });
                goalsUnsubs.set(patient.id, goalsUnsub);
              }
            });

            const completedUnsub = subscribeCompletedQuestions(
              patient.id,
              (completed) => {
                setCompletedByPatient((prev) => ({ ...prev, [patient.id]: completed }));
              },
              (error) => {
                // Log error silently
              }
            );

            activeUnsubs.set(patient.id, activeUnsub);
            completedUnsubs.set(patient.id, completedUnsub);
          });
        },
        (error) => {
          // Only show error after 3 retry attempts
          retryCount += 1;
          if (retryCount <= 3) {
            // Exponential backoff: 2s, 4s, 8s
            const delay = Math.pow(2, retryCount) * 1000;
            retryTimeout = setTimeout(() => {
              setupSubscriber();
            }, delay);
          } else {
            setIsLoadingPatients(false);
            setUiError("Live updates unavailable. Please refresh the page or try again in a moment.");
          }
        }
      );
      return unsubscribePatients;
    };

    let unsubscribePatients = setupSubscriber();

    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      if (unsubscribePatients) unsubscribePatients();
      cleanupPatientSubs();
    };
  }, [user.id]);

  const getActiveQuestionForPatient = (patId) => activeByPatient[patId] || null;

  React.useEffect(() => {
    let cancelled = false;

    const loadCompletedDetails = async () => {
      if (!selectedPatient?.id) {
        return;
      }

      const completed = completedByPatient[selectedPatient.id] || [];

      const summaryPairs = await Promise.all(
        completed.map(async (q) => {
          const summaryText = await getSummary(q.id).catch((err) => {
            return null;
          });
          return [q.id, summaryText || ""];
        })
      );

      if (cancelled) return;
      setSummaryByQuestion((prev) => ({
        ...prev,
        ...Object.fromEntries(summaryPairs),
      }));
    };

    loadCompletedDetails();

    return () => {
      cancelled = true;
    };
  }, [selectedPatient?.id, completedByPatient]);

  const submitQuestion = async () => {
    if (!qText.trim() || !selectedPatient) return;
    const existing = getActiveQuestionForPatient(selectedPatient.id);
    if (existing) return;

    setUiError("");
    setProcessing(true);
    let stage = "createQuestion";
    let qId = null;

    try {
      qId = await createQuestion(user.id, selectedPatient.id, qText);
      setQText("");
      refresh();

      // Decompose question into goals
      stage = "decomposeQuestion";
      const goalTexts = await decomposerAgent(qText);

      stage = "createGoals";
      await createGoals(qId, goalTexts);

      // Create conversation
      stage = "createConversation";
      const convId = await createConversation(qId, selectedPatient.id);

      stage = "attachConversationToQuestion";
      await attachConversationToQuestion(qId, convId);

      // Get opening message from AI
      stage = "generateOpeningMessage";
      const openingResult = await conversationAgent({
        currentGoal: goalTexts[0],
        remainingGoals: goalTexts.slice(1),
        messages: [],
        memoryChunks: [],
        patientName: selectedPatient.name,
      });

      stage = "addOpeningMessage";
      await addMessage(convId, { role: "ai", text: openingResult.text, time: now() });

      stage = "updateQuestionStatus";
      await updateQuestionStatus(qId, "collecting");

      setProcessing(false);
      refresh();
    } catch (error) {
      // If decomposer failed, mark question as goal_failed and let doctor retry
      if (stage === "decomposeQuestion" && qId) {
        await updateQuestionStatus(qId, "goal_failed");
        setUiError("We could not generate goals right now. Please click Regenerate Goals and try again in about 60 seconds.");
      } else {
        setUiError("We could not submit your question right now. Please try again.");
      }
      setProcessing(false);
      refresh();
    }
  };

  const regenerateGoals = async () => {
    if (!activeQ || !selectedPatient) return;

    setUiError("");
    setProcessing(true);

    try {
      // Retry decomposer
      const goalTexts = await decomposerAgent(activeQ.text);

      // Create goals
      await createGoals(activeQ.id, goalTexts);

      // Create conversation
      const convId = await createConversation(activeQ.id, selectedPatient.id);

      // Attach conversation
      await attachConversationToQuestion(activeQ.id, convId);

      // Get opening message from AI
      const openingResult = await conversationAgent({
        currentGoal: goalTexts[0],
        remainingGoals: goalTexts.slice(1),
        messages: [],
        memoryChunks: [],
        patientName: selectedPatient.name,
      });

      // Add opening message
      await addMessage(convId, { role: "ai", text: openingResult.text, time: now() });

      // Update status to collecting
      await updateQuestionStatus(activeQ.id, "collecting");

      setUiError("");
      setProcessing(false);
      refresh();
    } catch (error) {
      setUiError("Goal generation is still unavailable. Please try again shortly.");
      setProcessing(false);
    }
  };

  const pat = selectedPatient;
  const activeQ = pat ? getActiveQuestionForPatient(pat.id) : null;
  const completedQs = pat ? completedByPatient[pat.id] || [] : [];



  const navItems = [
    { id: "patients", label: "Patients", active: page === "patients", onClick: () => setPage("patients"), status: myPatients.length > 0 ? "active" : "idle" },
    { id: "settings", label: "Settings", active: page === "settings", onClick: () => setPage("settings") },
  ];

  return (
    <div className="mx-auto flex max-w-7xl pt-[4.25rem] lg:grid lg:min-h-screen lg:grid-cols-[18rem_minmax(0,1fr)] lg:pt-0">
      <Sidebar
        logoText="Doctor Portal"
        role="doctor"
        user={user}
        nav={navItems}
        onLogout={onLogout}
      />

      <div className="w-full overflow-y-auto pb-24 lg:pb-0">
        {page === "patients" && (
          <div className="mx-auto w-full max-w-5xl px-4 py-5 sm:px-6 sm:py-7">
            <div className="text-3xl font-semibold tracking-tight text-slate-800">Your Patients</div>
            <div className="mb-5 mt-3 text-sm font-medium text-slate-600">Select a patient to review, then submit a question to begin assessment.</div>

            <div className="mb-6 overflow-hidden rounded-2xl border border-sky-100 bg-white shadow-sm">
              {isLoadingPatients && <div className="px-4 py-10 text-center text-sm text-slate-500">Loading patients...</div>}
              {myPatients.map((p) => {
                const aq = getActiveQuestionForPatient(p.id);
                return (
                  <div
                    key={p.id}
                    className={`flex cursor-pointer items-center justify-between border-b border-sky-50 px-4 py-4 transition last:border-b-0 ${selectedPatient?.id === p.id ? "bg-sky-50" : "hover:bg-slate-50"}`}
                    onClick={() => setSelectedPatient(p)}
                  >
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{p.name}</div>
                      <div className="mt-0.5 text-xs text-slate-500">Age {p.age || "—"} · Joined {p.joinedDate}</div>
                    </div>
                    {aq ? (
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${aq.status === "collecting" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                        {aq.status}
                      </span>
                    ) : (
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-500">idle</span>
                    )}
                  </div>
                );
              })}
              {myPatients.length === 0 && (
                <div className="px-4 py-10 text-center text-sm text-slate-500">
                  No patients yet. Share your doctor code: <span className="mx-1 inline-block rounded-xl border border-sky-200 bg-sky-50 px-3 py-1 font-mono text-base tracking-[0.16em] text-sky-700">{user.doctorCode}</span>
                </div>
              )}
            </div>

            {pat && (
              <div>
                <div className="rounded-2xl border border-sky-100 bg-white p-4 shadow-sm sm:p-5">
                  <div className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                    {pat.name} — Active Query
                  </div>

                  {!activeQ && (
                    <div className="mb-1">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Ask a clinical question</div>
                      <textarea
                        className="min-h-[120px] w-full resize-y rounded-xl border border-sky-100 bg-white px-3.5 py-3 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
                        value={qText}
                        onChange={(e) => setQText(e.target.value)}
                        placeholder="e.g. I want to understand Alex's sleep, anxiety levels, and social behavior this week..."
                      />
                      <div className="mt-3 flex justify-end">
                        <button
                          className="rounded-xl bg-gradient-to-r from-sky-600 to-emerald-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-sky-200 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                          onClick={submitQuestion}
                          disabled={processing || !qText.trim()}
                        >
                          {processing ? "Processing..." : "Submit Question"}
                        </button>
                      </div>
                    </div>
                  )}

                  {activeQ && activeQ.status === "goal_failed" && (
                    <div>
                      <div className="mb-3 text-sm leading-relaxed text-slate-600">
                        <span className="font-medium text-slate-500">Question: </span>
                        {activeQ.text}
                      </div>
                      <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 p-3">
                        <div className="text-sm text-rose-700">
                          ⚠️ Failed to generate goals from your question. This usually happens when API quota is exceeded.
                        </div>
                        <button
                          onClick={regenerateGoals}
                          disabled={processing}
                          className="mt-3 rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {processing ? "Regenerating..." : "🔄 Regenerate Goals"}
                        </button>
                      </div>
                    </div>
                  )}

                  {activeQ && activeQ.status !== "goal_failed" && (
                    <div>
                      <div className="mb-3 text-sm leading-relaxed text-slate-600">
                        <span className="font-medium text-slate-500">Question: </span>
                        {activeQ.text}
                      </div>
                      <div className="mb-3 flex items-center gap-2.5">
                        <div
                          className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${activeQ.status === "collecting" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}
                        >
                          {activeQ.status}
                        </div>
                        {activeQ.status === "collecting" && <div className="h-4 w-4 animate-spin rounded-full border-2 border-sky-200 border-t-sky-500" />}
                      </div>

                      {/* Goals */}
                      {(goalsByQuestion[activeQ.id] || []).length > 0 && (
                        <div>
                          <div className="mb-1.5 text-xs font-medium text-slate-500">
                            Goals
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {(goalsByQuestion[activeQ.id] || []).map((g) => (
                              <div
                                key={g.id}
                                className={`rounded-full border px-3 py-1 text-xs font-medium ${g.status === "done" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : g.status === "active" ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}
                              >
                                {g.status === "done" ? "✓ " : g.status === "active" ? "● " : ""}
                                {g.text}
                              </div>
                            ))}
                          </div>
                          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-sky-100">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-all duration-300"
                              style={{
                                width: `${((goalsByQuestion[activeQ.id] || []).filter((g) => g.status === "done").length / (goalsByQuestion[activeQ.id] || []).length) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="mt-2 text-xs text-slate-500">
                        Our AI conversation agent is currently guiding the patient. Once the goal questions are answered, your results and summary will appear here.
                      </div>

                    </div>
                  )}
                </div>

                {completedQs.length > 0 ? (
                  <div className="mt-6">
                    <div className="mb-3 text-sm font-semibold text-slate-800">
                      Completed Sessions ({completedQs.length})
                    </div>
                    {completedQs.map((q) => (
                      <div key={q.id} className="mb-3 rounded-2xl border border-sky-100 bg-white p-4 shadow-sm">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                          Completed
                        </div>
                        <div className="mb-3 text-sm text-slate-800">
                          {q.text}
                        </div>
                        {q.id in summaryByQuestion && summaryByQuestion[q.id]?.trim() && (
                          <div className="mb-3">
                            <div className="mb-1.5 text-xs font-medium text-slate-500">Summary</div>
                            <div className="text-sm leading-relaxed text-slate-700">
                              {summaryByQuestion[q.id]}
                            </div>
                          </div>
                        )}
                        {q.id in summaryByQuestion && !summaryByQuestion[q.id]?.trim() && (
                          <div className="mb-3">
                            <div className="text-xs text-slate-500">No summary available yet.</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-6 text-center text-sm text-slate-500">
                    No completed sessions yet.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {page === "settings" && (
          <div className="mx-auto w-full max-w-5xl px-4 py-5 sm:px-6 sm:py-7">
            <div className="text-3xl font-semibold tracking-tight text-slate-800">Settings</div>
            <div className="mb-5 mt-1 text-sm text-slate-500">Your account information</div>
            <div className="rounded-2xl border border-sky-100 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4">
                <div className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Name
                </div>
                <div className="text-sm text-slate-800">{user.name}</div>
              </div>
              <div className="mb-4">
                <div className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Email
                </div>
                <div className="text-sm text-slate-800">{user.email}</div>
              </div>
              <div>
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Your Patient Invite Code
                </div>
                <div className="inline-flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-1">
                  <span className="font-mono text-base tracking-[0.16em] text-sky-700">{user.doctorCode}</span>
                  <button
                    type="button"
                    onClick={copyDoctorCode}
                    className="rounded-lg border border-sky-200 bg-white px-2 py-1 text-xs font-semibold text-sky-700 transition hover:bg-sky-100"
                  >
                    {copiedInviteCode ? "Copied!" : "Copy"}
                  </button>
                </div>
                <div className="mt-1.5 text-xs text-slate-500">
                  Share this with patients during onboarding
                </div>
              </div>
              <div className="mt-5">
                <button
                  type="button"
                  onClick={onLogout}
                  className="w-full rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ErrorModal message={uiError} onClose={() => setUiError("")} />
    </div>
  );
};
